import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LogCardComponent, LogService } from '@context/frontend/log';
import { MediaRowComponent, MediaService } from '@context/frontend/media';
import { PageTemplateComponent } from '@context/frontend/navigation';
import { PaginatedObject } from '@context/frontend/pagination';
import { StorageService } from '@context/frontend/storage';
import { TablePlaceholderComponent } from '@context/frontend/table';
import { PageCardComponent } from '@context/frontend/ui/components';
import { Content } from '@context/shared/types/common';
import { ContentFile } from '@context/shared/types/file';
import { ContentLog } from '@context/shared/types/log';
import {
	ContentMedia,
	getPreferredThumbnail,
} from '@context/shared/types/media';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiItem } from '@taiga-ui/cdk';
import { TuiButton, TuiIcon, TuiLoader } from '@taiga-ui/core';
import { TuiTabs, TuiTabsWithMore } from '@taiga-ui/kit';
import { DocumentReference, where } from 'firebase/firestore';
import { ExternalViewerNavService } from '../../../services/external-viewer-nav.service';
import { ExternalContentResolved } from '../external-content.resolver';

type ViewerTab = 'logs' | 'media';

/**
 * Read-only file details for the external viewer.
 *
 * Mirrors the portal's "view" access experience: the thumbnail, name and
 * description plus the file's logs and media. Chemical usage logs, owner and status
 * are intentionally omitted because the share session is not permitted to read them.
 */
@Component({
	standalone: true,
	selector: 'ctx-external-file-view',
	imports: [
		RouterLink,
		TranslocoModule,
		LogCardComponent,
		MediaRowComponent,
		TablePlaceholderComponent,
		PageCardComponent,
		TuiButton,
		TuiIcon,
		TuiLoader,
		TuiTabs,
		TuiTabsWithMore,
		TuiItem,
		PageTemplateComponent,
	],
	templateUrl: 'external-file-view.component.html',
	styleUrl: 'external-file-view.component.scss',
	host: { class: 'block h-full w-full overflow-auto' },
})
export class ExternalFileViewComponent {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly storageService = inject(StorageService);
	private readonly logService = inject(LogService);
	private readonly mediaService = inject(MediaService);
	protected readonly nav = inject(ExternalViewerNavService);

	readonly file = signal<ContentFile | null>(null);
	readonly fileRef = signal<DocumentReference<Content> | null>(null);
	readonly thumbnailUrl = signal<string | null>(null);
	readonly backFolderId = signal<string | null>(null);

	readonly tab = signal<ViewerTab>('logs');
	readonly tabIndex = signal(0);

	readonly logs = signal<
		{ data: ContentLog; ref: DocumentReference<ContentLog> }[]
	>([]);
	readonly logsLoading = signal(false);
	private logsLoaded = false;

	readonly media = signal<
		{ data: ContentMedia; ref: DocumentReference<ContentMedia> }[]
	>([]);
	readonly mediaLoading = signal(false);
	private mediaLoaded = false;

	constructor() {
		this.route.data
			.pipe(takeUntilDestroyed())
			.subscribe((data) => this.onRouteData(data['content']));
	}

	private onRouteData(resolved: ExternalContentResolved | null) {
		if (!resolved) return;

		// A folder id reached the file route - send the viewer to the folder view.
		if (resolved.data.type !== 'file') {
			this.router.navigate(this.nav.contentLink(resolved.data), {
				replaceUrl: true,
			});
			return;
		}

		const file = resolved.data as ContentFile;
		this.file.set(file);
		this.fileRef.set(resolved.ref);
		this.backFolderId.set(this.nav.parentWithinBounds(file));

		this.logsLoaded = false;
		this.mediaLoaded = false;
		this.tab.set('logs');
		this.tabIndex.set(0);

		void this.determineThumbnail(file);
		void this.loadLogs();
	}

	onTabChange(index: number) {
		this.tabIndex.set(index);
		const tab: ViewerTab = index === 1 ? 'media' : 'logs';
		this.tab.set(tab);
		if (tab === 'media') void this.loadMedia();
		else void this.loadLogs();
	}

	private async determineThumbnail(file: ContentFile) {
		const thumbnail = getPreferredThumbnail(file.thumbnails, {
			resolution: 2048,
		});
		if (!thumbnail) {
			this.thumbnailUrl.set(null);
			return;
		}

		try {
			const ref = this.storageService.getReference(thumbnail.storagePath);
			this.thumbnailUrl.set(
				await this.storageService.getDownloadUrl(ref),
			);
		} catch (error) {
			console.error('Failed to load file thumbnail', error);
			this.thumbnailUrl.set(null);
		}
	}

	private async loadLogs() {
		const fileRef = this.fileRef();
		if (!fileRef || this.logsLoaded) return;

		this.logsLoading.set(true);
		try {
			const paginated = await this.logService.fetch(
				new PaginatedObject<ContentLog>({
					parentRef: fileRef,
					sortBy: 'createdAt',
					sortDir: 'desc',
					pageSize: 100,
					constraints: [where('archivedAt', '==', null)],
				}),
			);
			this.logs.set([...paginated.items]);
			this.logsLoaded = true;
		} catch (error) {
			console.error('Failed to load file logs', error);
			this.logs.set([]);
		} finally {
			this.logsLoading.set(false);
		}
	}

	private async loadMedia() {
		const fileRef = this.fileRef();
		if (!fileRef || this.mediaLoaded) return;

		this.mediaLoading.set(true);
		try {
			const paginated = await this.mediaService.fetch(
				new PaginatedObject<ContentMedia>({
					parentRef: fileRef,
					sortBy: 'name',
					sortDir: 'asc',
					pageSize: null,
					constraints: [where('archivedAt', '==', null)],
				}),
			);
			this.media.set([...paginated.items]);
			this.mediaLoaded = true;
		} catch (error) {
			console.error('Failed to load file media', error);
			this.media.set([]);
		} finally {
			this.mediaLoading.set(false);
		}
	}
}
